import { useEffect, useRef, useState } from 'react';

const Dropdown = ({
    options = [],
    placeholder = '-',
    onChange = () => {},
    name,
    className = '',
    disabled = false,
    value = '',
    id,
    checkCondition,
    setCheckSubwayPosts,
    openUpwards,
    fixedUpwards,
    allowEmpty = false, // true: cho phép chọn option có value rỗng ('') để bỏ chọn
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('');
    const [selectedValue, setSelectedValue] = useState(value); // Thêm state để lưu giá trị `value`
    const [isOpenUpwards, setIsOpenUpwards] = useState(false); // Trạng thái dropdown mở lên trên
    const dropdownRef = useRef(null);

    useEffect(() => {
        const matchedOption = options.find((option) => option.value === value || option.option === value);

        if (matchedOption) {
            setSelectedOption(matchedOption.option);
            setSelectedValue(matchedOption.value);
        }
    }, [value, options]);

    // Mở hoặc đóng dropdown
    const toggleDropdown = () => {
        if (!disabled) {
            setIsDropdownOpen(!isDropdownOpen);
        }
    };

    // Đóng dropdown khi click ra ngoài
    const closeDropdown = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsDropdownOpen(false);
        }
    };

    // Xử lý khi chọn option
    const handleOptionClick = (option) => {
        if (disabled) return;
        if (setCheckSubwayPosts && name) {
            setCheckSubwayPosts((prev) => ({
                ...prev,
                [name]: false,
            }));
        }
        setSelectedOption(option.option); // Lưu tên option
        setSelectedValue(option.value); // Lưu giá trị value của option
        setIsDropdownOpen(false);

        // Kiểm tra nếu giá trị là URL
        if (
            typeof option.value === 'string' &&
            option.value &&
            (option.value.startsWith('http://') || option.value.startsWith('https://'))
        ) {
            window.open(option.value, '_blank'); // Mở URL trong tab mới
        } else {
            // Nếu không phải URL, gọi hàm onChange với giá trị `value`
            if (option.value || (allowEmpty && option.value === '')) onChange(option.value, name); // Gọi hàm onChange và truyền giá trị `value` của option
        }
    };

    useEffect(() => {
        // Gán sự kiện click ngoài để đóng dropdown
        document.addEventListener('click', closeDropdown);

        return () => {
            document.removeEventListener('click', closeDropdown);
        };
    }, []);

    // Kiểm tra không gian còn lại phía dưới và điều chỉnh hướng dropdown
    const checkDropdownPosition = () => {
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const optionHeight = dropdownRef.current.querySelector('.form-option-wrapper')?.clientHeight || 0;

            // Nếu không gian dưới không đủ, mở dropdown lên trên
            if (openUpwards === false) {
                setIsOpenUpwards(false);
            } else {
                if (spaceBelow < optionHeight) {
                    setIsOpenUpwards(true);
                } else {
                    setIsOpenUpwards(false);
                }
            }
        }
    };

    useEffect(() => {
        if (isDropdownOpen) {
            checkDropdownPosition();
        }
        // eslint-disable-next-line
    }, [isDropdownOpen]);

    const handleKeyDown = (event) => {
        // Kiểm tra nếu phím được nhấn là Enter
        if (event.key === 'Enter') {
            event.preventDefault();
            toggleDropdown();
        }
    };
    const handleBlur = (event) => {
        // event.relatedTarget là phần tử sắp nhận focus
        // Kiểm tra xem relatedTarget có tồn tại và có nằm BÊN TRONG dropdownRef hay không
        if (dropdownRef.current && !dropdownRef.current.contains(event.relatedTarget)) {
            // Nếu focus di chuyển ra ngoài dropdown container, thì đóng dropdown
            // Dùng setTimeout để xử lý trường hợp click vào option (blur xảy ra trước click)
            // Nếu không có timeout, click vào option có thể bị coi là blur ra ngoài trước khi click được xử lý
            setTimeout(() => {
                // Kiểm tra lại isDropdownOpen trước khi đóng, phòng trường hợp nó đã bị đóng bởi việc khác
                if (isDropdownOpen) {
                    setIsDropdownOpen(false);
                }
            }, 0);
        }
        // Nếu focus di chuyển vào một phần tử bên trong dropdown (ví dụ: nếu option cũng có thể focus), không làm gì cả
    };
    return (
        <div
            tabIndex={disabled ? -1 : 0}
            role={disabled ? undefined : 'button'}
            onKeyDown={disabled ? undefined : handleKeyDown}
            onBlur={handleBlur}
            className={`form-select-container ${isDropdownOpen ? 'active' : ''} ${
                isOpenUpwards ? 'open-upwards' : ''
            } ${className || ''}`}
        >
            <div className="form-select-cus" ref={dropdownRef} id={id}>
                <div className="form-option-placeholder" onClick={toggleDropdown}>
                    {checkCondition
                        ? placeholder || (allowEmpty && options.find((option) => option.value === '')?.option) || ''
                        : selectedOption === ''
                          ? placeholder
                          : selectedOption}
                </div>
                {!disabled && (
                    <div className="form-option-wrapper">
                        {placeholder !== '' && (
                            <div className="form-option-placeholder-container">
                                <div className="form-option-placeholder">{placeholder}</div>
                            </div>
                        )}
                        <div className="form-option-container" name={name}>
                            {/* option.hidden: vẫn nằm trong options để hiển thị text đã chọn, nhưng không cho chọn lại */}
                            {options.filter((option) => !option.hidden).map((option, index) => (
                                <div
                                    key={index}
                                    className={`form-option ${selectedValue === option.value ? 'active' : ''}`} // So sánh với `selectedValue`
                                    onClick={() => handleOptionClick(option)} // Truyền toàn bộ option vào
                                >
                                    {option.option} {/* Hiển thị tên option */}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dropdown;
